import { BUILT_IN_ITEMS } from './schema'
import type { BuiltInItem, LocalizedText, NavLink, PortalCustomization, PortalNavigation } from './schema'

/** A resolved sidebar item (ClientPortal `PortalNavigationItem`); `id` is a built-in item ref. */
export interface NavItemInput {
  id: string
  name: string
  navGroup: string
}

/** A sidebar group in display order; the first one is the header-less top band. */
export interface NavGroupInput {
  groupId: string
  labelKey?: string
}

// Type aliases (not interfaces) so link items fit sidebar item types that carry an index signature.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- an interface has no implicit index signature
export type LinkTarget = {
  href: string
  /** Leaves the ClientPortal router (full page load). */
  external: boolean
  newTab: boolean
}

export type LinkNavItem = LinkTarget & {
  id: `link:${string}`
  name: string
  iconName: string
  navGroup: string
  routeName: ''
  routePath: string
}

export interface NavGroupOutput<T> {
  groupId: string
  labelKey?: string
  /** Already-localized label (custom groups, or a relabelled built-in group); wins over labelKey. */
  label?: string
  items: Array<T | LinkNavItem>
}

/** Picks the current locale's text: exact locale, then its language, then the first value. */
export function localize(text: LocalizedText, locale: string): string {
  if (typeof text === 'string') return text
  return text[locale] ?? text[locale.split(/[-_]/)[0]] ?? Object.values(text)[0] ?? ''
}

/** Where a custom link goes: in-portal links render at `/links/<id>`, the rest leave the portal router. */
export function linkTarget(id: string, link: NavLink): LinkTarget {
  if (link.open === 'in-portal') return { href: `/links/${id}`, external: false, newTab: false }
  return { href: link.url, external: true, newTab: link.open === 'new-tab' }
}

/**
 * Applies `navigation` to the sidebar ClientPortal already resolved (accessible items, its groups):
 * hidden items, labels, custom groups and group assignment, item `order`, `groupOrder`, and custom
 * links. Unknown refs and groups are ignored; empty groups are dropped.
 */
export function applyNavigation<T extends NavItemInput>(
  items: T[],
  groups: NavGroupInput[],
  navigation: PortalNavigation | undefined,
  locale: string
): NavGroupOutput<T>[] {
  const settings = navigation?.items ?? {}
  const customGroups = navigation?.groups ?? {}
  const topGroupId = groups[0]?.groupId

  const outputGroups: NavGroupOutput<T>[] = [
    ...groups.map(group => {
      const custom = customGroups[group.groupId]
      return { ...group, ...(custom && { label: localize(custom.label, locale) }), items: [] }
    }),
    ...Object.entries(customGroups)
      .filter(([groupId]) => !groups.some(group => group.groupId === groupId))
      .map(([groupId, group]) => ({ groupId, label: localize(group.label, locale), items: [] })),
  ]
  const groupById = new Map(outputGroups.map(group => [group.groupId, group]))

  const links: LinkNavItem[] = Object.entries(navigation?.links ?? {}).map(([id, link]) => {
    const target = linkTarget(id, link)
    return {
      id: `link:${id}`,
      name: localize(link.label, locale),
      iconName: link.icon,
      navGroup: topGroupId ?? '',
      routeName: '',
      routePath: target.href,
      ...target,
    }
  })

  const order = navigation?.order ?? []
  const rank = (ref: string) => {
    const index = order.indexOf(ref as (typeof order)[number])
    return index === -1 ? order.length : index
  }

  const visible = [...items, ...links].filter(item => !settings[item.id]?.hidden).sort((a, b) => rank(a.id) - rank(b.id))

  for (const item of visible) {
    const assigned = settings[item.id]?.group
    const groupId = assigned === null ? topGroupId : assigned && groupById.has(assigned) ? assigned : item.navGroup
    const label = settings[item.id]?.label
    const group = groupById.get(groupId ?? '') ?? groupById.get(topGroupId ?? '')
    group?.items.push(label ? { ...item, name: localize(label, locale) } : item)
  }

  const groupOrder = navigation?.groupOrder ?? []
  const groupRank = (groupId: string) => {
    const index = groupOrder.indexOf(groupId)
    return index === -1 ? groupOrder.length : index
  }

  return outputGroups.filter(group => group.items.length > 0).sort((a, b) => groupRank(a.groupId) - groupRank(b.groupId))
}

/** `navigation.landing` → a built-in item, a link or Records page target, or null (unset / unknown / not applied yet). */
export function resolveLanding(
  navigation: PortalNavigation | undefined,
  records?: PortalCustomization['records']
): { item: BuiltInItem } | LinkTarget | null {
  const ref = navigation?.landing
  if (!ref) return null
  if ((BUILT_IN_ITEMS as readonly string[]).includes(ref)) return { item: ref as BuiltInItem }
  const slug = ref.startsWith('records:') ? ref.slice('records:'.length) : undefined
  if (slug)
    return records?.views && Object.hasOwn(records.views, slug) ? { href: `/records/${slug}`, external: false, newTab: false } : null
  const link = ref.startsWith('link:') ? navigation?.links?.[ref.slice('link:'.length)] : undefined
  return link ? linkTarget(ref.slice('link:'.length), link) : null
}
